/**
 * Blog post data source.
 *
 * Each post lives here as a plain JS object with the body written in Markdown-lite:
 *  - Lines starting with "## " are H2 (rendered with anchor + colored heading)
 *  - Lines starting with "- " are list items (grouped into <ul>)
 *  - Lines starting with "> " are highlighted checklist / tip callouts
 *  - Blank line = new paragraph
 *  - **word** = bold, *word* = italic, [text](url) = link
 *
 * Add a new post by pushing a new object to POSTS (top-most = newest).
 */

export const POSTS = [
  {
    slug: "id-doudou-etiquette-thermocollante-autocollante",
    title: "L'ID Doudou Alert : créez l'étiquette qui ramène le doudou à la maison",
    excerpt:
      "Chaque doudou enregistré sur Doudou Alert reçoit désormais un numéro ID unique. Découvrez à quoi il sert, comment le faire imprimer sur une étiquette thermocollante ou autocollante, et comment l'utiliser si vous trouvez un doudou.",
    date: "2026-10-15",
    readingMinutes: 4,
    category: "Nouveauté",
    tags: ["nouveauté", "étiquette doudou", "ID doudou", "prévention"],
    keywords:
      "étiquette doudou, étiquette thermocollante doudou, marquer doudou, étiquette autocollante doudou, identifiant doudou, retrouver doudou perdu",
    body: `Bonne nouvelle : **chaque doudou enregistré sur Doudou Alert possède désormais son propre numéro ID**, unique et permanent. Un petit code de 8 caractères qui peut tout changer le jour où le doudou se fait la malle.

## À quoi sert le numéro ID ?

L'ID est la carte d'identité de votre doudou. Il ressemble à ceci : \`AB3X-K9MZ\`.

- **Il est unique** : aucun autre doudou ne porte le même, et il n'est jamais réattribué, même si une fiche est supprimée
- **Il est permanent** : il ne change pas, vous pouvez l'imprimer une fois pour toutes
- **Il est anonyme** : il ne contient ni le prénom de votre enfant, ni votre adresse, ni votre téléphone
- **Il est facile à lire** : nous avons retiré les caractères qui se confondent (0 et O, 1, I et L)

Grâce à cet ID, la personne qui trouve le doudou peut retrouver directement sa fiche, sans chercher sur la carte, et vous contacter via notre chat anonyme.

> **Où trouver l'ID de votre doudou ?** Connectez-vous, ouvrez **Mes doudous** : l'ID apparaît sur chaque fiche, avec un bouton pour le copier en un clic.

## Faire fabriquer votre étiquette

Pas besoin de matériel : il suffit de commander des étiquettes personnalisées sur un **site spécialisé en étiquettes pour enfants ou en étiquettes personnalisées**. Deux types conviennent parfaitement :

- **Étiquette thermocollante** : elle se pose au fer à repasser et résiste aux lavages. Idéale pour les doudous en tissu
- **Étiquette autocollante** : très simple à poser, parfaite pour les doudous en matière lisse ou sur une étiquette d'origine

Au moment de personnaliser votre étiquette, **écrivez seulement 2 lignes** :

- **Ligne 1** : l'ID de votre doudou (par exemple \`AB3X-K9MZ\`)
- **Ligne 2** : doudou-alert.fr

[[etiquette]]

> **Important pour la sécurité de votre enfant** : n'ajoutez jamais son nom complet, votre adresse ou votre numéro de téléphone sur l'étiquette. L'ID suffit, c'est tout l'intérêt.

## Coller l'étiquette sur le doudou

L'étiquette est faite pour être **collée directement sur le doudou** :

- Choisissez un endroit **visible mais discret** : sous un bras, dans le dos, sur la patte ou sur l'étiquette d'origine
- Pour une étiquette thermocollante, suivez les indications du fabricant (température du fer, temps de pose) et protégez le doudou avec un linge fin
- Pour une étiquette autocollante, posez-la sur une surface propre et sèche, et appuyez bien quelques secondes
- Vérifiez de temps en temps qu'elle est toujours bien lisible, surtout après les passages en machine

## Vous avez trouvé un doudou avec une étiquette ?

Merci ! Vous êtes à quelques clics de rendre un enfant très heureux :

- Rendez-vous sur la page [J'ai trouvé un doudou](/trouve)
- **Saisissez le code** inscrit sur l'étiquette dans l'encadré « Le doudou a une étiquette Doudou Alert ? », tout en haut de la page. Pas besoin de taper le tiret : il s'ajoute tout seul après les 4 premiers caractères
- Vous arrivez sur la page du doudou : **ajoutez une photo du doudou trouvé**, votre prénom, votre email et un petit message
- La famille est **prévenue immédiatement par email** et vous échangez avec elle via le chat anonyme, sans jamais donner votre adresse

---

> **En résumé** : un ID unique, une étiquette à 2 lignes, et votre doudou a toutes les chances de revenir à la maison. Pas encore de fiche ? [Créez-la gratuitement en 2 minutes](/inscription) pour obtenir l'ID de votre doudou.
`,
  },
  {
    slug: "doudou-perdu-creche-nounou",
    title: "Doudou perdu à la crèche ou chez la nounou : les bons réflexes",
    excerpt:
      "Votre enfant est rentré sans son doudou ? Voici les 6 étapes à suivre immédiatement, ce qu'il faut demander à la crèche et comment maximiser vos chances de le récupérer dès demain matin.",
    date: "2026-09-01",
    readingMinutes: 6,
    category: "Où chercher",
    tags: ["crèche", "nounou", "assistante maternelle", "doudou perdu"],
    keywords:
      "doudou perdu crèche, doudou oublié chez la nounou, doudou perdu assistante maternelle, retrouver doudou crèche",
    body: `Le soir arrive, l'enfant réclame son doudou pour dormir et... il n'est nulle part. Un rapide échange avec la crèche ou la nounou et le doute s'installe : le doudou est resté quelque part entre l'accueil, la salle de sieste, la cour et le vestiaire. Pas de panique — dans 9 cas sur 10, il réapparaît sous 48 heures **à condition d'agir vite et bien**.

## Étape 1 — Contacter la structure immédiatement (même le soir)

Ne pas attendre le lendemain matin. Beaucoup de crèches ferment leur bâtiment à 19h et le ménage passe entre 20h et 22h. Un doudou oublié sous un lit ou dans un coin peut finir dans la poubelle du nettoyage si personne n'a été prévenu.

- Appelez le numéro général de la crèche, laissez un message vocal si nécessaire
- Envoyez **en parallèle** un email ou WhatsApp à l'assistante maternelle
- Décrivez précisément : couleur, taille, marque si connue, prénom éventuellement brodé
- **Envoyez une photo récente** — c'est le meilleur moyen d'accélérer la recherche

## Étape 2 — Passer à la crèche dès l'ouverture le lendemain

> Astuce : arrivez 10 minutes avant l'ouverture officielle. Le personnel a le temps de vous accompagner dans les salles avant l'arrivée des autres familles.

Demandez à faire le tour physique des lieux avec un membre de l'équipe :

- La salle de sieste (sous le lit, dans le lit voisin, entre le matelas et le mur)
- Le vestiaire (dans un autre casier par erreur)
- La cour extérieure (dans le bac à sable, sous les jeux)
- La salle d'accueil (canapés, coussins, meubles bas)
- Les objets trouvés (souvent une boîte discrète près de l'entrée)

## Étape 3 — Diffuser une alerte avec photo

Signalez le doudou sur [Doudou Alert](/inscription) avec une photo, la description et l'adresse de la crèche. En quelques minutes, la fiche est visible sur la [carte des doudous signalés](/doudous-trouves) et toute personne travaillant à proximité peut recevoir une alerte s'il est retrouvé.

## Étape 4 — Prévenir les autres parents

- Demandez à la crèche de faire circuler l'info dans le groupe WhatsApp des parents (avec la photo)
- Publiez sur le groupe Facebook local "Parents [ville]" ou "Objets perdus [département]"
- Affichez une petite annonce papier plastifiée à l'entrée de la crèche (avec accord de la direction)

## Étape 5 — Vérifier les affaires ramenées à la maison

Le doudou n'est peut-être pas resté à la crèche. Vérifiez :

- Le sac de change / affaires que la crèche vous a remis
- Le siège auto (glissé entre l'assise et le dossier)
- La poussette (dans les paniers du dessous, entre les coussins)
- Le manteau et les poches de l'enfant

## Étape 6 — Cas où le doudou ne réapparaît pas sous 48h

Restez calme, plusieurs pistes restent ouvertes :

- Contactez le **service d'entretien** de la crèche (souvent une société extérieure)
- Demandez à consulter les **objets trouvés de la mairie** si la crèche est communale
- Élargissez la zone de recherche à la [carte publique Doudou Alert](/doudous-trouves) — d'autres familles ont pu le récupérer et attendent que quelqu'un se manifeste

> **Checklist rapide à imprimer** :
> 1. Appel ce soir + email avec photo
> 2. Visite physique demain à l'ouverture
> 3. Signalement sur Doudou Alert
> 4. Groupe WhatsApp parents + Facebook local
> 5. Affiche papier à l'entrée
> 6. Vérification sac, poussette, siège auto

## Éviter que ça se reproduise

Beaucoup de familles nous partagent la même astuce : avoir **un doudou de secours identique** stocké à la maison. En attendant, [inscrivez le doudou sur Doudou Alert](/inscription) — sa fiche restera dormante et pourra être réactivée en 1 clic si un jour il disparaît à nouveau.
`,
  },
  {
    slug: "doudou-perdu-train-gare-sncf",
    title: "Doudou perdu dans le train ou à la gare : que faire ?",
    excerpt:
      "TGV, TER, gare de transit… Un doudou oublié dans un train peut être retrouvé, mais il faut connaître les bons interlocuteurs et agir dans les 24-48h. Voici le protocole complet.",
    date: "2026-09-01",
    readingMinutes: 7,
    category: "Où chercher",
    tags: ["train", "SNCF", "gare", "TGV", "TER"],
    keywords:
      "doudou perdu train, doudou oublié TGV, objets trouvés SNCF, doudou perdu gare, retrouver doudou train",
    body: `Un enfant qui s'endort dans le TGV avec son doudou dans les bras… et qui se réveille sans lui en descendant. Ça arrive à des centaines de familles chaque année. La bonne nouvelle : la SNCF dispose d'un service objets trouvés très structuré, à condition de le solliciter rapidement.

## Étape 1 — Rester dans la gare de descente si possible

Si vous vous rendez compte de la perte immédiatement après avoir quitté le train :

- **Ne quittez pas la gare** et courez au **Point Info / accueil SNCF**
- Précisez : le numéro du train, l'heure d'arrivée, la voiture (indiquée sur votre billet), le siège
- Le personnel peut parfois faire un appel radio au chef de bord si le train n'a pas encore reparti

> **Info précieuse** : le numéro de train figure sur votre billet (ex. "TGV 8412", "TER 878734"). Sans ce numéro, la recherche est bien plus lente.

## Étape 2 — Déclarer via le site officiel SNCF Objets Trouvés

Le service est centralisé sur **[objets-trouves.sncf.com](https://www.objets-trouves.sncf.com/)** :

- Créez une déclaration en indiquant type d'objet ("jouet / peluche"), couleur, taille, matière
- **Ajoutez une photo** — c'est ce qui déclenche la comparaison automatique avec les objets récupérés
- Indiquez le train, la date, la voiture, le siège
- Vous recevrez un numéro de dossier

Les objets trouvés dans les trains sont centralisés dans les gares de terminus (Paris Gare de Lyon, Marseille Saint-Charles, Lyon Part-Dieu, Toulouse Matabiau, etc.). **La restitution peut se faire à distance, envoyée à vos frais** (~15-25 € en Chronopost).

## Étape 3 — Passer un appel au 3635 (SNCF Info)

En parallèle de la déclaration en ligne :

- Composez le **3635** (0,40€/min)
- Demandez le service "objets trouvés" (touche 3 dans le menu)
- Indiquez vos coordonnées pour être rappelée si l'objet est identifié

## Étape 4 — Contacter la gare terminus du train

Si votre train allait plus loin après votre descente, le doudou est probablement descendu à la gare terminus. Chaque grande gare a un numéro objets trouvés dédié (à trouver via [sncf.com](https://www.sncf.com)).

- Décrivez le doudou en détail
- Envoyez la photo par email si le personnel accepte

## Étape 5 — Signaler sur Doudou Alert

Un contrôleur, un agent de nettoyage ou un autre voyageur a peut-être récupéré le doudou en dehors du circuit officiel. Signalez-le sur [Doudou Alert](/inscription) avec :

- La ville de descente + la ville de départ du train
- Une photo bien nette
- La date et le train si connus

La [carte des doudous signalés](/doudous-trouves) permet aux personnes qui traversent cette même ligne de faire le rapprochement.

## Étape 6 — Groupes Facebook & réseaux

- Groupe **"Objets perdus / retrouvés SNCF"** (très actif, plusieurs milliers de membres)
- Groupes régionaux "Objets perdus [ville de descente]"
- Twitter/X en interpellant **@SNCF** avec la photo et le numéro de train (parfois très efficace en journée)

## Délais et taux de récupération

- **Sous 24h** : 60% des objets déclarés sont retrouvés
- **Sous 48h** : 75%
- **Au-delà de 3 jours** : les objets non réclamés sont regroupés à Paris et gardés 3 mois. Passé ce délai, ils sont détruits ou donnés à des associations.

> **Checklist urgente si vous êtes encore en gare** :
> 1. Point Info SNCF avec numéro de train + voiture + siège
> 2. Déclaration sur objets-trouves.sncf.com (avec photo)
> 3. Appel au 3635
> 4. Contact gare terminus
> 5. Signalement Doudou Alert + groupe Facebook SNCF

## Éviter que ça se reproduise

- Attachez toujours le doudou à une **dragonne** clipsable sur la ceinture ou le sac (technique éprouvée par des milliers de parents)
- Placez une **petite étiquette cousue** avec votre prénom + numéro (jamais l'adresse) — signalé sur [Doudou Alert](/inscription), ce doudou pourra être rendu 10x plus vite
- Photographiez le doudou avant chaque voyage — la photo servira si besoin
`,
  },
  {
    slug: "doudou-perdu-parc-balade-rue",
    title: "Doudou perdu au parc, en balade ou dans la rue : la méthode qui marche",
    excerpt:
      "En balade, au square, dans la poussette qui traverse la ville… Un doudou peut tomber sans qu'on s'en rende compte. Voici comment quadriller le terrain et mobiliser le voisinage.",
    date: "2026-09-01",
    readingMinutes: 5,
    category: "Où chercher",
    tags: ["parc", "rue", "balade", "extérieur", "affichage"],
    keywords:
      "doudou perdu parc, doudou tombé poussette, doudou perdu rue, retrouver doudou balade, affiche doudou perdu",
    body: `À la différence de la crèche ou du train, un doudou perdu dehors n'a pas d'interlocuteur unique. Il faut donc **quadriller vous-même le parcours** et **mobiliser le voisinage**. La bonne nouvelle : les gens qui trouvent un doudou dans la rue le posent presque toujours sur un banc, un rebord de fenêtre ou un poteau bien visible — dans l'espoir qu'un parent le récupère.

## Étape 1 — Refaire le trajet en sens inverse, vite

Chaque minute compte. Si le doudou est tombé il y a moins de 30 minutes, il est probablement encore là où il est tombé.

- Refaites **exactement le même parcours** — passages piétons, poussette dans la même direction
- Regardez **sous les bancs, dans les massifs, sous les voitures garées**
- Vérifiez la **poussette elle-même** (glissé entre l'assise et le dossier)

## Étape 2 — Regarder en hauteur, pas en bas

C'est le réflexe des passants bienveillants : quand ils trouvent un doudou par terre, ils le posent **en hauteur** pour qu'il soit visible et propre.

- Rebord de fenêtre au rez-de-chaussée
- Muret, poteau, borne
- Bord d'une jardinière
- Grillage de parc (accroché aux barreaux)
- Sur un banc, jamais dessous

## Étape 3 — Interpeller les commerçants du trajet

Les boulangeries, cafés, pharmacies, tabacs sont les premiers refuges des doudous ramassés :

- Montrez une photo du doudou
- Laissez votre prénom + numéro écrit à la main (une petite étiquette suffit)
- Demandez si on peut afficher la photo à côté de la caisse

## Étape 4 — Afficher (imprimer une affiche en 5 minutes)

Une affiche A4 simple et efficace, plastifiée si possible (sinon pochette transparente). Contenu :

- **Photo grande et nette** du doudou (occupe la moitié de l'affiche)
- Titre en gros : \`"J'AI PERDU MON DOUDOU"\`
- Sous-titre : \`"Perdu le [date] entre [lieu A] et [lieu B]"\`
- Court texte : \`"C'est le compagnon irremplaçable de [prénom de l'enfant, âge]. Merci de m'appeler / m'écrire si vous le retrouvez."\`
- Un numéro de téléphone **ou** l'URL raccourcie de la fiche Doudou Alert
- Une phrase émotionnelle : \`"Une petite fille compte sur vous 🧡"\`

**Emplacements** : lampadaires, arrêts de bus, poteaux à hauteur d'yeux (jamais sur la vitrine d'un commerce sans accord).

## Étape 5 — Signaler sur Doudou Alert

Créez la fiche sur [Doudou Alert](/inscription) — l'alerte s'affiche automatiquement sur la [carte des doudous signalés](/doudous-trouves) et toute personne du quartier voit apparaître un pin orange à l'endroit exact où le doudou a été perdu. Une personne qui trouverait le doudou peut aussi ouvrir la carte sans créer de compte et voir votre alerte.

## Étape 6 — Groupes Facebook du quartier

- **"Tu sais que tu habites à [ville] quand…"** (très actif dans toutes les villes)
- **"Objets trouvés / perdus [ville]"**
- **"Parents de [quartier]"**

Publiez avec **photo + lieu approximatif + jour + votre message**. Beaucoup de gens ont vu quelque chose sans oser publier eux-mêmes.

## Étape 7 — Mairie et services de propreté

Souvent oublié : les agents de propreté municipaux ramassent parfois des doudous et les déposent au service objets trouvés de la mairie. Contactez :

- Le **service d'accueil de la mairie** (souvent un email dédié aux objets trouvés)
- Le **standard de la Police Municipale** si vous êtes en centre-ville

> **Checklist rapide** :
> 1. Retour immédiat sur le parcours (en sens inverse)
> 2. Regarder en hauteur, pas en bas
> 3. Commerçants du trajet + photo
> 4. Affiche A4 avec photo grande
> 5. Fiche Doudou Alert
> 6. Groupes Facebook du quartier
> 7. Mairie / Police Municipale

## Éviter que ça se reproduise

Le vent, les cahots de la poussette, un enfant qui lance son doudou "juste pour voir"… La perte extérieure est la plus fréquente. Deux astuces qui changent tout :

- **Un porte-tétine XL clipsable** entre le doudou et la barre de la poussette
- **Un mini AirTag ou un Chipolo** cousu discrètement à l'intérieur (pour les doudous "précieux entre tous")

Et bien sûr, la fiche préventive sur [Doudou Alert](/inscription) — 2 minutes maintenant, 2 heures gagnées le jour où il disparaît.
`,
  },
  {
    slug: "10-astuces-ne-jamais-perdre-doudou",
    title: "10 astuces pour ne jamais perdre le doudou de son enfant",
    excerpt:
      "La meilleure façon de retrouver un doudou est de ne pas le perdre. Voici 10 astuces éprouvées par des milliers de parents, du plus simple à mettre en place au plus technique.",
    date: "2026-09-01",
    readingMinutes: 8,
    category: "Prévention",
    tags: ["prévention", "astuces", "conseils", "parents"],
    keywords:
      "éviter perdre doudou, astuces doudou perdu, marquer doudou enfant, doudou tracker, sauvegarder doudou",
    body: `On ne réalise jamais à quel point on tenait à ce doudou tant qu'il n'a pas disparu. Voici 10 astuces classées de la plus simple à la plus technique, pour transformer un doudou fragile en compagnon indestructible.

## 1. Photographier le doudou sous toutes les coutures

Le tout premier réflexe, à faire aujourd'hui, avant tout drame :

- Photo de face, photo de dos, photo des étiquettes
- Photo à côté d'une pièce de 1€ pour l'échelle
- Photo en plein soleil pour restituer les vraies couleurs
- Photo en gros plan des signes distinctifs (tache, couture, bouton manquant…)

Ces photos vous serviront pour : Doudou Alert, la SNCF, les affiches, les groupes Facebook. **Sans photo, la recherche est 5x plus lente.**

## 2. Créer sa fiche préventive sur Doudou Alert

Deux minutes suffisent pour créer une fiche dormante sur [Doudou Alert](/inscription). Elle contient photo + description + prénom. Le jour où le doudou disparaît, vous cliquez sur "Je l'ai perdu" et l'alerte se diffuse en 1 clic. Sinon, la fiche reste privée.

## 3. Avoir un doudou de secours strictement identique

L'astuce numéro 1 partagée par les crèches. Deux options :

- **Acheter deux doudous identiques dès la naissance** et les faire tourner (l'enfant ne s'attache pas à un seul, ils s'usent au même rythme)
- **Acheter un modèle de rechange dès que possible**, à laisser à la maison "en réserve"

> Marques qui reproduisent leurs modèles longtemps : Sauthon, Kaloo, Nattou, Doudou et Compagnie, Ikea. Certaines commercialisent le même doudou pendant 15 ans.

## 4. Marquer discrètement le doudou

**Ne jamais écrire d'adresse ni de nom d'enfant complet** (sécurité). Mais faire ceci :

- Coudre une petite étiquette brodée avec un **prénom + numéro de téléphone**
- OU y coudre l'URL courte de la fiche Doudou Alert
- OU appliquer une **étiquette thermocollante** avec juste "Signalé sur doudou-alert.fr"

Une personne qui trouve un doudou marqué sait immédiatement comment le rendre. **Le taux de restitution passe de 40% à plus de 90%** avec un marquage.

## 5. Utiliser une dragonne / porte-tétine XL

Pour tous les déplacements (poussette, siège auto, train, restaurant) :

- Clip d'un côté sur la ceinture, la anse de la poussette, la boucle du siège auto
- De l'autre côté cousu sur une petite tirette du doudou (sous le bras, dans le dos)
- Longueur 30-40 cm pour laisser de la liberté

## 6. Ajouter un tracker Bluetooth

Pour les doudous "les plus précieux", envisager :

- **Chipolo Card** (fin comme une carte de crédit, à glisser dans une poche cousue)
- **Apple AirTag** (si iPhone dans la famille — précision très bonne mais 25€ pièce)
- **Tile** (compatible Android, précision moyenne mais réseau vaste)

Le doudou devient localisable via smartphone dans un rayon de 50-100m, et via le réseau Apple/Tile n'importe où en France.

## 7. Établir la règle "le doudou reste dans le sac"

Beaucoup de familles adoptent cette règle : **le doudou n'est jamais dans les mains de l'enfant dehors**. Il vit dans le sac / la poussette / le sac à dos, et n'en sort qu'à la maison, chez la nounou, au restaurant.

- Réduit de 80% le risque de perte pendant les déplacements
- L'enfant apprend à faire la distinction "dedans / dehors"
- Beaucoup de nounous et crèches recommandent cette approche

## 8. Ranger dans une "boîte à doudous" à la maison

Un endroit unique et connu : \`un panier en osier près du canapé\`, une \`boîte cartonnée décorée par l'enfant\`, une \`poche fixée près du lit\`. Résultat :

- L'enfant sait où le retrouver seul
- Vous savez toujours où il est le matin en partant
- On évite le "doudou perdu sous le canapé pendant 4 jours"

## 9. Faire tourner plusieurs doudous

L'astuce des orthophonistes : **avoir 3 à 5 doudous en rotation**. L'enfant crée un attachement multiple ("mes doudous") au lieu d'un attachement exclusif à un seul objet. Perdre l'un des 5 est triste, mais bien moins traumatisant.

## 10. En vacances : le protocole de la valise

Avant chaque départ :

- **Photographier le doudou dans la valise fermée** (preuve de départ)
- **Le nommer sur une check-list papier** collée à l'intérieur de la valise
- **Vérifier au check-out** : dernière ronde de la chambre d'hôtel / gîte / camping
- **Photographier chaque hébergement** en partant (photo panoramique) — utile pour se souvenir "où on l'a vu pour la dernière fois"

---

> **La règle d'or** : si votre enfant a un doudou irremplaçable, **il ne peut plus être irremplaçable**. Achetez le double, marquez-le, photographiez-le, signalez-le préventivement. C'est 30 minutes de préparation pour éviter des jours d'angoisse.

Prêt·e à créer la fiche préventive de votre doudou ? [Ça se passe ici, en 2 minutes.](/inscription)
`,
  },
];

export function getPostBySlug(slug) {
  return POSTS.find((p) => p.slug === slug) || null;
}

export function getRelatedPosts(currentSlug, limit = 3) {
  return POSTS.filter((p) => p.slug !== currentSlug).slice(0, limit);
}
